/**
 * Canonical scenario defaults for the calculator UI.
 *
 * Every value shown to a user must have a citation attached. `SCENARIO_SOURCES`
 * is the single source of truth for tooltip/methodology copy; the form loads
 * defaults from `SCENARIO_DEFAULTS` and renders the matching source string as
 * the tooltip on each input label. Changing a default is a code change in this
 * file and must ship with the citation update in the same commit — never
 * silently.
 *
 * The scenario-level values here live alongside — not inside — the math-engine
 * defaults (`MORTGAGE_DEFAULTS`, `BREAK_EVEN_DEFAULTS`) because they describe
 * the *market conditions* the user is modelling, not the mechanics of the
 * mortgage product or the recurring-cost structure. Keeping them separated
 * matches the file layout of docs/design.md §3 and lets the engine tests stay
 * independent of UI copy.
 */

import {
  BREAK_EVEN_DEFAULTS,
  type BreakEvenInputs,
} from "./math/breakEven";
import { US_NATIONAL } from "./math/jurisdictions";
import { MORTGAGE_DEFAULTS } from "./math/mortgage";

/** Source citation for one field. Rendered verbatim in the tooltip. */
export interface SourceCitation {
  readonly label: string;
  readonly url: string;
  readonly note: string;
}

/**
 * Citations for every default the UI surfaces. Keys must match
 * `SCENARIO_DEFAULTS` (and, for math-engine values, DEFAULT_SOURCES in
 * `math/mortgage.ts`).
 */
export const SCENARIO_SOURCES = {
  monthlyRent: {
    label: "US Census ACS — median gross rent",
    url: "https://data.census.gov/table?q=B25064",
    note: "US median gross rent from the 2023 American Community Survey (5-year estimate). Rounded to the nearest $50.",
  },
  homePrice: {
    label: "NAR — median existing-home sale price",
    url: "https://www.nar.realtor/research-and-statistics/housing-statistics/existing-home-sales",
    note: "National Association of Realtors monthly existing-home sales report; national median for single-family homes.",
  },
  horizonYears: {
    label: "NAR — median seller tenure",
    url: "https://www.nar.realtor/research-and-statistics/research-reports/highlights-from-the-profile-of-home-buyers-and-sellers",
    note: "NAR Profile of Home Buyers and Sellers: recent sellers had lived in their home a median of 10 years; first-time buyers turn over sooner (~7 years). We anchor on the shorter figure because it is the more common decision horizon.",
  },
  annualHomeAppreciationPercent: {
    label: "FHFA House Price Index — long-run US average",
    url: "https://www.fhfa.gov/data/hpi",
    note: "Compound annual growth rate of the FHFA seasonally-adjusted purchase-only HPI over the last 30 years. Long-run figure to avoid over-weighting recent volatility.",
  },
  annualRentInflationPercent: {
    label: "BLS CPI — rent of primary residence",
    url: "https://www.bls.gov/cpi/",
    note: "Ten-year CAGR of the BLS Consumer Price Index for rent of primary residence, national.",
  },
  annualDiscountRatePercent: {
    label: "S&P 500 — long-run nominal return",
    url: "https://www.spglobal.com/spdji/en/indices/equity/sp-500/",
    note: "Approximate nominal long-run return of a diversified US equity portfolio. Editable — use 5% for a conservative bond-heavy mix, 8% for aggressive equity-only.",
  },
  annualPropertyTaxRatePercent: {
    label: "Tax Foundation — effective property-tax rates",
    url: "https://taxfoundation.org/data/all/state/property-taxes-by-state/",
    note: "US national average effective property-tax rate on owner-occupied housing (~1.1%). State overlays run from ~0.3% (Hawaii) to ~2.2% (New Jersey).",
  },
  annualHomeInsurance: {
    label: "Insurance Information Institute — homeowners insurance",
    url: "https://www.iii.org/fact-statistic/facts-statistics-homeowners-and-renters-insurance",
    note: "US national average annual homeowner's insurance premium for a standard HO-3 policy. Editable — coastal, wildfire, or high-value locations run considerably higher.",
  },
  annualMaintenanceRatePercent: {
    label: "1% rule — long-run maintenance reserve",
    url: "https://www.jchs.harvard.edu/improving-americas-housing",
    note: "Rule-of-thumb reserve of ~1% of home value per year covers routine repairs, systems replacement, and periodic renovation. Harvard JCHS Improving America's Housing reports also cluster around this figure.",
  },
  monthlyHOA: {
    label: "US Census AHS — HOA / condo fees",
    url: "https://www.census.gov/programs-surveys/ahs.html",
    note: "American Housing Survey: most single-family homes have no HOA. Median HOA where present is around $200/mo; luxury / condo buildings can be materially higher. Zero by default.",
  },
  annualRentersInsurance: {
    label: "Insurance Information Institute — renters insurance",
    url: "https://www.iii.org/fact-statistic/facts-statistics-homeowners-and-renters-insurance",
    note: "Approximately $15/mo national average renter's insurance premium, per Insurance Information Institute survey data.",
  },
} as const satisfies Record<string, SourceCitation>;

/**
 * Scenario-level defaults (market conditions and headline user inputs).
 * The `jurisdiction` field defaults to the US national baseline; the six
 * primary above-the-fold inputs are: `monthlyRent`, `homePrice`,
 * `downPaymentPercent`, `annualInterestRatePercent`, `horizonYears`,
 * `annualHomeAppreciationPercent`. Everything else is behind the
 * "show defaults" toggle.
 */
export const SCENARIO_DEFAULTS = {
  // Six primary inputs.
  monthlyRent: 1500,
  homePrice: 420_000,
  downPaymentPercent: MORTGAGE_DEFAULTS.downPaymentPercent,
  annualInterestRatePercent: MORTGAGE_DEFAULTS.annualInterestRatePercent,
  horizonYears: 7,
  annualHomeAppreciationPercent: 3.5,
  // Behind the toggle.
  termYears: MORTGAGE_DEFAULTS.termYears,
  annualRentInflationPercent: 3.0,
  annualDiscountRatePercent: 7.0,
  annualPropertyTaxRatePercent: BREAK_EVEN_DEFAULTS.annualPropertyTaxRatePercent,
  annualHomeInsurance: BREAK_EVEN_DEFAULTS.annualHomeInsurance,
  annualMaintenanceRatePercent: BREAK_EVEN_DEFAULTS.annualMaintenanceRatePercent,
  monthlyHOA: BREAK_EVEN_DEFAULTS.monthlyHOA,
  annualRentersInsurance: BREAK_EVEN_DEFAULTS.annualRentersInsurance,
  jurisdictionId: US_NATIONAL.id,
  /**
   * Indefinite-hold flag (REN-13 edge case). Off by default — the honest
   * comparison bakes in the friction of selling — but a user who is buying
   * their forever home can toggle it in the assumptions panel and see the
   * numbers with no sell-side commission subtracted.
   */
  neverSell: false,
} as const;

export type ScenarioDefaults = typeof SCENARIO_DEFAULTS;

/**
 * All numeric scenario inputs, in a shape the form can drive directly. The
 * `jurisdictionId` is a string key resolved to a `Jurisdiction` object at
 * compute time so the URL stays stable across future jurisdiction-table
 * edits.
 */
export interface ScenarioInputs {
  monthlyRent: number;
  homePrice: number;
  downPaymentPercent: number;
  annualInterestRatePercent: number;
  horizonYears: number;
  annualHomeAppreciationPercent: number;
  termYears: number;
  annualRentInflationPercent: number;
  annualDiscountRatePercent: number;
  annualPropertyTaxRatePercent: number;
  annualHomeInsurance: number;
  annualMaintenanceRatePercent: number;
  monthlyHOA: number;
  annualRentersInsurance: number;
  jurisdictionId: string;
  neverSell: boolean;
}

/** Return a fresh mutable copy of the defaults for the form to hydrate. */
export function makeDefaultScenario(): ScenarioInputs {
  return { ...SCENARIO_DEFAULTS };
}

/** Numeric keys of `ScenarioInputs` — everything except the non-numeric fields. */
export type NumericScenarioKey = Exclude<
  keyof ScenarioInputs,
  "jurisdictionId" | "neverSell"
>;

/**
 * Convert a `ScenarioInputs` (form state) into a `BreakEvenInputs` (math
 * engine input) by resolving the jurisdiction id against the given lookup.
 * Returns `null` if the jurisdiction id is unknown, so the caller can surface
 * a validation error rather than crashing the engine.
 */
export function toBreakEvenInputs(
  scenario: ScenarioInputs,
  resolveJurisdiction: (id: string) => BreakEvenInputs["jurisdiction"] | undefined,
): BreakEvenInputs | null {
  const jurisdiction = resolveJurisdiction(scenario.jurisdictionId);
  if (!jurisdiction) return null;
  const { jurisdictionId: _unused, neverSell, ...numeric } = scenario;
  return { ...numeric, jurisdiction, neverSell };
}
